import 'components/ordering/quotation/components/request/error-banner.js'
import 'components/ordering/quotation/components/request/footer.js'
import 'components/ordering/quotation/components/request/form.js'
import 'components/ordering/quotation/components/request/header.js'
import 'components/ordering/quotation/components/request/lines.js'
import 'components/ordering/quotation/components/request/total-price.js'
import { CSSResultGroup, html, LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { hostBlockStyles } from 'components/ordering/quotation/components/quotation.style.js'
import { QuotationDTO } from 'components/ordering/quotation/api/dtos/quotation-dto.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { NotFoundError } from 'components/ordering/quotation/api/dtos/error.js'
import { consume } from '@lit/context'
import {
  activeQuotationContext,
  getQuotationByIdContext,
  NotFetchedYet
} from 'components/ordering/quotation/components/context/quotation-context.js'
import { required } from 'components/shared/required/required.js'
import 'components/ordering/quotation/components/request/accepted-banner.js'
import 'components/ordering/quotation/components/request/information.js'
import 'components/ordering/quotation/components/request/note-merchant.js'


@customElement('csc-quotation-request')
export class CSCQuotationRequest extends LitElement {
  static override styles: CSSResultGroup = [hostBlockStyles]
  @required()
  @property({ type: Number, reflect: true, attribute: 'quotation-id' })
  quotationId!: number

  @consume({ context: activeQuotationContext, subscribe: true })
  private quotation!: QuotationDTO | NotFoundError | NotFetchedYet

  @consume({ context: getQuotationByIdContext })
  private getQuotationById!: (quotationId: number) => Promise<void>

  private previousQuotationId: number | undefined

  get noOpenQuotationsFound(): boolean {
    return this.quotation instanceof NotFoundError
  }

  get gridWithForm(): boolean {
    return this.quotation instanceof QuotationDTO && this.quotation.status === 'open'
  }

  get gridWithInfoBlock(): boolean {
    return this.quotation instanceof QuotationDTO
      && this.quotation.status !== 'open'
      && !!(this.quotation.externalReference
      || this.quotation.validityDate
      || this.quotation.name
      || this.quotation.note
      || this.quotation.reasonOfDecline)
  }

  get gridWithMerchantNote(): boolean {
    return this.quotation instanceof QuotationDTO
      && this.quotation.status !== 'open'
      && !!this.quotation.noteMerchant
  }

  override async connectedCallback() {
    super.connectedCallback()
    this.previousQuotationId = this.quotationId
    try {
      await this.getQuotationById(this.quotationId)
    } catch (error) {
      console.error('[csc-quotation-request] connectedCallback error:', error)
    }
  }

  override willUpdate(changedProperties: Map<string | number | symbol, unknown>) {
    super.willUpdate(changedProperties)
    if (changedProperties.has('quotationId') && this.previousQuotationId !== this.quotationId) {
      this.previousQuotationId = this.quotationId
      this.getQuotationById(this.quotationId).catch(error => {
        console.error('[csc-quotation-request] willUpdate error:', error)
      })
    }
  }

  renderNoQuotationFound() {
    this.classList.add('grid-with-no-quotations-found')

    return html`
      <cse-alert variant="info">
        ${t('no_quotation_found_title', {
          ns: 'csc-quotation-request',
          default: 'There are no open quotations'
        })}
        <div slot="description">
          ${t('no_quotation_found_description', {
            ns: 'csc-quotation-request',
            default: 'At the moment you don’t have an open quotation to be shown. If you have any questions about this, please contact us via the link below.'
          })}
        </div>
        <cse-link slot="link" url="${t('no_quotation_found_contact_url', {
          ns: 'csc-quotation-request',
          default: '/contact'
        })}">
          ${t('no_quotation_found_contact_text', {
            ns: 'csc-quotation-request',
            default: 'Contact'
          })}
        </cse-link>
      </cse-alert>
    `
  }

  override render() {
    return html`
      <cse-group direction="column" row-gap="08">
        <csc-quotation-request-header></csc-quotation-request-header>
        <csc-quotation-error-banner></csc-quotation-error-banner>
        ${this.noOpenQuotationsFound ?
          this.renderNoQuotationFound() : html`
            <csc-quotation-request-form></csc-quotation-request-form>
            <csc-quotation-request-accepted-banner></csc-quotation-request-accepted-banner>
            <csc-quotation-request-information></csc-quotation-request-information>
            <csc-quotation-note-merchant></csc-quotation-note-merchant>
            <csc-quotation-lines></csc-quotation-lines>
            <csc-quotation-total-price></csc-quotation-total-price>
            <csc-quotation-request-footer></csc-quotation-request-footer>
          `
        }
      </cse-group>
    `
  }
}
